import { useRequest } from 'ahooks';
import classNames from 'classnames';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Overlay, OverlayProps } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { FaThumbsDown } from 'react-icons/fa';
import styled from 'styled-components';

import { usePreferences } from '../../hooks/usePreferences';
import { useMembership } from '../../hooks/users';
import { miscService } from '../../services';
import LoadingImage from '../general/LoadingImage';
import Separator from '../Separator';

const LikeOverlayDiv = styled.div`
  min-width: 20rem;
  min-height: 50px;
  max-height: 80vh;
  background-color: #000000cc;
  color: white;
  font-size: 0.8rem;
  z-index: 1051; /* bootstrap modal has z-index 1050 */
  overflow-y: auto;
  padding: 1rem;
  border-radius: 0.2rem;
  transition: background-color ease-in-out 0.5s;

  &.loading {
    background-color: #ffffffcc;
    border: solid 1px #888;
  }
`;
const RAISE_COLOR = 'rgb(0, 204, 0)';
const renderChange = item =>
  item.price ? (
    <div
      style={{
        marginLeft: '5px',
        color: item.updatedPrice - item.price >= 0 ? RAISE_COLOR : 'red',
      }}
    >
      {item.updatedPrice - item.price >= 0 ? '+' : '-'}
      {Math.abs(((item.updatedPrice - item.price) / item.price) * 100).toFixed(
        2
      )}
      %
    </div>
  ) : (
    <div
      style={{
        marginLeft: '5px',
        color: RAISE_COLOR,
      }}
    >
      -%
    </div>
  );

const itemLatestLikeTimeSorting = (a, b) => {
  return new Date(b.likeTime).getTime() - new Date(a.likeTime).getTime();
};

interface Props {
  stockCode: string;
  children: ReactElement;
  placement?: OverlayProps['placement'];
}
const LikeOverlay: React.FC<Props> = ({
  stockCode,
  children,
  placement = 'right',
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t } = useTranslation();
  const hasMembership = useMembership();

  const {
    data: loadedData,
    loading,
    run: fetch,
  } = useRequest(miscService.getUserListForStockOptions(stockCode), {
    manual: true,
  });

  const { showWatchingList } = usePreferences();

  const data = loadedData || {
    list: [],
    anonymous: null,
  };

  const { list, anonymous } = data;
  const noUserLike = list.length === 0;
  const [show, setShow] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const [leaveDelay, setLeaveDelay] = useState(null);
  const [enterDelay, setEnterDelay] = useState(null);
  const handleMouseEnter = useCallback(() => {
    clearTimeout(leaveDelay);

    // delay 0.5s to open the hover
    setEnterDelay(setTimeout(() => setShow(true), 500));
  }, [leaveDelay]);

  useEffect(() => {
    if ((show || forceShow) && hasMembership) {
      fetch();
    }
  }, [fetch, forceShow, hasMembership, show]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(enterDelay);

    // give user enough time to move the cursor from "children" to overlay
    setLeaveDelay(setTimeout(() => setShow(false), 100));
  }, [enterDelay]);
  const ref = useRef();

  return (
    <>
      <div
        style={{ display: 'inline' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={ref}
      >
        {children}
      </div>
      {showWatchingList && (
        <Overlay
          target={ref.current}
          show={show || forceShow}
          placement={placement}
        >
          <LikeOverlayDiv
            className={classNames('like-overlay', { loading })}
            onMouseEnter={() => setForceShow(true)}
            onMouseLeave={() => setForceShow(false)}
          >
            <div style={{ textAlign: 'left', margin: '10px 0' }}>
              <LoadingImage loading={loading} displayAsBlock />
              {!loading && loadedData && (
                <>
                  <div
                    style={{
                      textAlign: 'center',
                      marginBottom: 10,
                    }}
                  >
                    {noUserLike
                      ? t('usl-stock-view-nouserlike-title')
                      : t('usl-stock-view-user-title')}
                  </div>
                  <Separator />
                  {noUserLike && <br />}
                  {list.some(item => item.user.vip) && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ textAlign: 'center', padding: '10px 0' }}>
                        {t('usl-member')}
                      </div>
                      {list
                        .filter(item => item.user.vip)
                        .sort(itemLatestLikeTimeSorting)
                        .map((item, index) => (
                          <div key={index} style={{ display: 'flex' }}>
                            {index + 1}. {item.user.name}
                            {item.unlike ? <FaThumbsDown /> : null} ($
                            {item.price || '-'}
                            {item.likeTime
                              ? '; ' +
                                moment
                                  .utc(item.likeTime)
                                  .local()
                                  .format('DD/MM')
                              : ';- '}
                            ) {renderChange(item)}
                          </div>
                        ))}
                    </div>
                  )}
                  {list.some(item => !item.user.vip) && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ textAlign: 'center', padding: '10px 0' }}>
                        {t('usl-free')}
                      </div>
                      {list
                        .filter(item => !item.user.vip)
                        .sort(itemLatestLikeTimeSorting)
                        .map((item, index) => (
                          <div key={index} style={{ display: 'flex' }}>
                            {index + 1}. {item.user.name}
                            {item.unlike ? <FaThumbsDown /> : null} ($
                            {item.price || '-'}
                            {item.likeTime
                              ? '; ' +
                                moment
                                  .utc(item.likeTime)
                                  .local()
                                  .format('DD/MM')
                              : '; -'}
                            ) {renderChange(item)}
                          </div>
                        ))}
                    </div>
                  )}
                  {anonymous && anonymous.numberOfLikes > 0 && (
                    <div style={{ marginLeft: '0.8rem' }}>
                      <Trans i18nKey="more-like">
                        {{ n: anonymous.numberOfLikes }}
                      </Trans>
                    </div>
                  )}
                  {anonymous && anonymous.numberOfUnlikes > 0 && (
                    <div style={{ marginLeft: '0.8rem' }}>
                      <Trans i18nKey="more-unlike">
                        {{ n: anonymous.numberOfUnlikes }}
                      </Trans>
                    </div>
                  )}
                </>
              )}
              <div
                style={{
                  textAlign: 'center',
                  color: '#888',
                  fontSize: '0.7rem',
                }}
              >
                {!hasMembership && (
                  <p>
                    <strong>{t('feature-requires-vip')}</strong>
                  </p>
                )}
                <i>
                  <Trans i18nKey="usl-explain" />
                </i>
              </div>
            </div>
          </LikeOverlayDiv>
        </Overlay>
      )}
    </>
  );
};

export default LikeOverlay;
